import React, { FormEvent, KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  ArrowRight,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  FileText,
  Loader2,
  MessageSquareText,
  RefreshCw,
  Send,
  Sparkles,
  UserRound,
} from "lucide-react";
import {
  ContractWorkflowChange,
  ContractWorkflowTurn,
  createConversation,
  getConversation,
  prepareContractWorkflowTurn,
  renameConversation,
  sendAssistantMessage,
} from "features/productAssistant/productAssistant.api";
import { AssistantMessage } from "features/productAssistant/types";
import { LoganProjectDraft } from "features/productAssistant/projectDraft";

type RailMode = "assist" | "chat" | "review";

export interface ContractHomeownerMatch {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

export interface ContractAssistantRailProps {
  expanded: boolean;
  autoFocus?: boolean;
  initialContext?: string;
  activeStep: number;
  projectId: string | number;
  projectName: string;
  projectReady: boolean;
  homeownerReady: boolean;
  locationReady: boolean;
  paymentReady: boolean;
  milestoneCount: number;
  milestoneTotal: number;
  hasContractRecord: boolean;
  hasContractDocument: boolean;
  contractorSigned: boolean;
  homeownerSigned: boolean;
  contractEditorOpen: boolean;
  editableFields: string[];
  projectSnapshot: LoganProjectDraft;
  onApplyChanges: (changes: ContractWorkflowChange[]) => void | Promise<void>;
  onLookupHomeowners: (email: string) => Promise<ContractHomeownerMatch[]>;
  onSelectHomeowner: (homeowner: ContractHomeownerMatch) => void | Promise<void>;
  onSaveAndContinue: () => void | Promise<unknown>;
  onOpenContractEditor: () => void;
  onExpandedChange: (expanded: boolean) => void;
  onNavigate: (step: number) => void;
}

interface ReadinessItem {
  id: string;
  label: string;
  detail: string;
  ready: boolean;
  step: number;
}

const stepNames: Record<number, string> = {
  1: "Project Info",
  2: "Milestones",
  3: "Subcontractor",
  4: "Contract",
  5: "Review",
};

const initialProjectQuestion: AssistantMessage = {
  id: "contract-project-intro",
  role: "assistant",
  message: "Could you briefly tell me about your project in your own words? Include what work needs to be done, where the project is, and any details you already know. You can also ask what any project or contract field means.",
  suggestions: ["What is a payment milestone?", "Why is contractor insurance required?"],
};

const ContractAssistantRail: React.FC<ContractAssistantRailProps> = ({
  expanded,
  autoFocus = false,
  initialContext = "",
  activeStep,
  projectId,
  projectName,
  projectReady,
  homeownerReady,
  locationReady,
  paymentReady,
  milestoneCount,
  milestoneTotal,
  hasContractRecord,
  hasContractDocument,
  contractorSigned,
  homeownerSigned,
  contractEditorOpen,
  editableFields,
  projectSnapshot,
  onApplyChanges,
  onLookupHomeowners,
  onSelectHomeowner,
  onSaveAndContinue,
  onOpenContractEditor,
  onExpandedChange,
  onNavigate,
}) => {
  const [mode, setMode] = useState<RailMode>("chat");
  const [conversationId, setConversationId] = useState("");
  const [messages, setMessages] = useState<AssistantMessage[]>([initialProjectQuestion]);
  const [input, setInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState("");
  const [pendingTurn, setPendingTurn] = useState<ContractWorkflowTurn | null>(null);
  const [expectedField, setExpectedField] = useState("");
  const [homeownerMatches, setHomeownerMatches] = useState<ContractHomeownerMatch[]>([]);
  const [actionLoading, setActionLoading] = useState(false);
  const initializedProjectRef = useRef("");
  const milestoneSuggestionRef = useRef("");
  const contractKickoffRef = useRef("");
  const messageEndRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const initialContextProcessedRef = useRef(false);
  const workflowSnapshotRef = useRef(projectSnapshot);
  const chatRequestInFlightRef = useRef(false);
  const editableFieldSet = useMemo(() => new Set(editableFields), [editableFields]);

  useEffect(() => {
    workflowSnapshotRef.current = projectSnapshot;
  }, [projectSnapshot]);

  const readinessItems = useMemo<ReadinessItem[]>(() => [
    { id: "project", label: "Project details", detail: projectReady ? "Project name and value added" : "Project name or value needs attention", ready: projectReady, step: 1 },
    { id: "homeowner", label: "Homeowner", detail: homeownerReady ? "Signer selected" : "Select the homeowner who will sign", ready: homeownerReady, step: 1 },
    { id: "location", label: "Job-site location", detail: locationReady ? "Address is complete" : "Complete and verify the address", ready: locationReady, step: 1 },
    { id: "payment", label: "Payment plan", detail: paymentReady ? `${milestoneCount} milestones · ${milestoneTotal}%` : milestoneCount ? `Milestones total ${milestoneTotal}%` : "Add milestones totaling 100%", ready: paymentReady, step: 2 },
    { id: "contract", label: "Contract document", detail: hasContractDocument ? "Document generated" : hasContractRecord ? "Draft saved; document not generated" : "Contract terms not completed", ready: hasContractDocument, step: 4 },
    { id: "contractor-signature", label: "Contractor signature", detail: contractorSigned ? "Signed" : "Required before homeowner signing", ready: contractorSigned, step: 4 },
    { id: "homeowner-signature", label: "Homeowner signature", detail: homeownerSigned ? "Signed" : "Not signed yet", ready: homeownerSigned, step: 4 },
  ], [contractorSigned, hasContractDocument, hasContractRecord, homeownerReady, homeownerSigned, locationReady, milestoneCount, milestoneTotal, paymentReady, projectReady]);

  const readyCount = readinessItems.filter((item) => item.ready).length;
  const nextItem = readinessItems.find((item) => !item.ready);
  const currentStepName = stepNames[activeStep] || "Contract";
  const workflowState = homeownerSigned
    ? "Fully executed"
    : contractorSigned
      ? "Awaiting homeowner"
      : hasContractDocument
        ? "Ready for contractor signature"
        : hasContractRecord
          ? "Contract draft"
          : projectId
            ? "Preparing contract"
            : "Preparing project";
  const choiceQuestion = getWorkflowChoiceQuestion(expectedField);
  const selectedChoiceValue = choiceQuestion
    ? String((projectSnapshot as unknown as Record<string, unknown>)[expectedField] || "")
    : "";

  const chatContinueAction = activeStep === 1 && projectReady && homeownerReady && locationReady
    ? {
        label: projectId ? "Save changes & continue to Milestones" : "Save & Continue to Milestones",
        detail: "Project information is complete. Save it before building the payment plan.",
      }
    : activeStep === 2 && paymentReady
      ? {
          label: "Save & Continue to Contract",
          detail: `${milestoneCount} milestones total 100%. Save the payment plan and continue.`,
        }
      : null;

  useEffect(() => {
    messageEndRef.current?.scrollIntoView({ block: "nearest" });
  }, [messages, chatLoading]);

  useEffect(() => {
    if (activeStep !== 4 || !contractEditorOpen || editableFields.length === 0) return;
    const kickoffKey = `${projectId || "new"}:contract`;
    if (contractKickoffRef.current === kickoffKey) return;
    const next = getNextContractQuestion(projectSnapshot, editableFieldSet);
    contractKickoffRef.current = kickoffKey;
    setMode("chat");
    setExpectedField(next.field);
    setMessages((current) => [
      ...current,
      {
        id: `contract-kickoff-${Date.now()}`,
        role: "assistant",
        message: `Let's complete the homeowner contract. ${next.question}`,
      },
    ]);
  }, [activeStep, contractEditorOpen, editableFieldSet, editableFields.length, projectId, projectSnapshot]);

  useEffect(() => {
    if (expectedField !== "homeownerSelection" || !projectSnapshot.homeownerId) return;
    const nextEditableQuestion = [
      { field: "projectName", missing: !projectSnapshot.projectName, question: "What should this project be called?" },
      { field: "scope", missing: !projectSnapshot.scope, question: "Briefly describe the work that needs to be completed." },
      { field: "zip", missing: !projectSnapshot.zip, question: "What is the job-site ZIP code?" },
      { field: "address", missing: !projectSnapshot.address, question: "What is the job-site street address?" },
      { field: "homeownerEmail", missing: !projectSnapshot.homeownerEmail, question: "What is the homeowner's email address?" },
      { field: "costOfProject", missing: !(Number(String(projectSnapshot.costOfProject || "").replace(/,/g, "")) > 0), question: "What is the total project value?" },
      { field: "milestones", missing: !projectSnapshot.milestones.length, question: "The project value is already set. Would you like me to prepare a milestone payment plan?" },
    ].find((item) => item.missing && editableFieldSet.has(item.field));
    const nextField = nextEditableQuestion?.field || "projectReview";
    const nextMessage = nextEditableQuestion
      ? `Homeowner confirmed. ${nextEditableQuestion.question}`
      : editableFields.length
        ? "Homeowner confirmed. All editable details in this section are complete."
        : "Homeowner confirmed. I won't ask for or change fields that are locked in this section.";
    setExpectedField(nextField);
    setMessages((current) => [
      ...current,
      { id: `workflow-homeowner-${Date.now()}`, role: "assistant", message: nextMessage },
    ]);
  }, [editableFieldSet, expectedField, projectSnapshot.costOfProject, projectSnapshot.homeownerId, projectSnapshot.milestones.length]);

  useEffect(() => {
    if (editableFieldSet.has("homeownerSelection")) return;
    setHomeownerMatches([]);
  }, [editableFieldSet]);

  useEffect(() => {
    if (!pendingTurn) return;
    const hasLockedChange = pendingTurn.proposedChanges.some((change) => !editableFieldSet.has(change.field));
    if (hasLockedChange) setPendingTurn(null);
  }, [editableFieldSet, pendingTurn]);

  useEffect(() => {
    if (expectedField !== "costOfProject") return;
    const hasProjectValue = Number(String(projectSnapshot.costOfProject || "").replace(/,/g, "")) > 0;
    if (!hasProjectValue && editableFieldSet.has("costOfProject")) return;
    const shouldPrepareMilestones = !projectSnapshot.milestones.length && editableFieldSet.has("milestones");
    const replacement = shouldPrepareMilestones
      ? "The project value is already set. Would you like me to prepare a milestone payment plan?"
      : "The project value is already set or locked, so I won't ask for it again.";
    setExpectedField(shouldPrepareMilestones ? "milestones" : "projectReview");
    setMessages((current) => current.map((message) =>
      message.role === "assistant" && message.message === "Homeowner confirmed. What is the total project value?"
        ? { ...message, message: replacement }
        : message
    ));
  }, [editableFieldSet, expectedField, projectSnapshot.costOfProject, projectSnapshot.milestones.length]);

  useEffect(() => {
    const suggestionKey = `${projectId || "new"}:milestones`;
    if (
      activeStep !== 2 ||
      !editableFieldSet.has("milestones") ||
      projectSnapshot.milestones.length > 0 ||
      pendingTurn ||
      milestoneSuggestionRef.current === suggestionKey
    ) return;

    milestoneSuggestionRef.current = suggestionKey;
    setMode("chat");
    setChatLoading(true);
    setChatError("");
    prepareContractWorkflowTurn(
      "Prepare a practical milestone payment plan for this project.",
      projectSnapshot,
      "milestones",
      editableFields
    ).then((turn) => {
      setExpectedField(turn.nextField);
      const milestoneChange = turn.proposedChanges.find((change) => change.field === "milestones");
      if (!milestoneChange) {
        milestoneSuggestionRef.current = "";
        setChatError("Logan could not prepare milestone suggestions. You can ask again or add them manually.");
        return;
      }
      setPendingTurn(turn);
      setMessages((current) => {
        const suggestionMessage: AssistantMessage = {
          id: `workflow-milestones-${Date.now()}`,
          role: "assistant",
          message: `There are no milestones yet, so I prepared a payment plan based on ${projectName || "the project"} and its scope. Review each step below and apply it when you are satisfied.`,
        };
        const lastMessage = current[current.length - 1];
        return lastMessage?.role === "assistant" && lastMessage.message === "Homeowner confirmed. All editable details in this section are complete."
          ? [...current.slice(0, -1), suggestionMessage]
          : [...current, suggestionMessage];
      });
    }).catch((error: any) => {
      milestoneSuggestionRef.current = "";
      setChatError(error?.response?.data?.message || "Logan could not prepare milestone suggestions. Try again in a moment.");
    }).finally(() => {
      setChatLoading(false);
    });
  }, [activeStep, editableFieldSet, editableFields, pendingTurn, projectId, projectSnapshot]);

  const openStep = (step: number) => {
    if (!projectId && step > 1) return;
    if (step === 4) {
      onOpenContractEditor();
      return;
    }
    onNavigate(step);
  };

  const initializeChat = async (manageLoading = true) => {
    const projectKey = String(projectId || "new");
    if (initializedProjectRef.current === projectKey && conversationId) return conversationId;
    if (manageLoading) setChatLoading(true);
    setChatError("");
    try {
      const storageKey = `loganContractConversation.${projectKey}`;
      const storedId = sessionStorage.getItem(storageKey) || "";
      const conversation = storedId
        ? await getConversation(storedId).catch(() => null)
        : null;
      const activeConversation = conversation || await createConversation(navigator.language || "en-US", "The Tradesmen's Collective");
      if (!conversation) {
        await renameConversation(activeConversation.id, `Contract workspace — ${projectName || "New project"}`.slice(0, 190)).catch(() => undefined);
      }
      sessionStorage.setItem(storageKey, activeConversation.id);
      initializedProjectRef.current = projectKey;
      setConversationId(activeConversation.id);
      setMessages((current) => current.length ? current : (activeConversation.messages || []));
      return activeConversation.id;
    } catch (_error) {
      setChatError("Logan chat could not be loaded. Your contract form is still available.");
      return "";
    } finally {
      if (manageLoading) setChatLoading(false);
    }
  };

  const requestGuideAnswer = async (question: string) => {
    const activeConversationId = conversationId || await initializeChat(false);
    if (!activeConversationId) throw new Error("Conversation unavailable");
    return sendAssistantMessage(activeConversationId, question, {
      route: window.location.pathname + window.location.search,
      module: "contracts",
      entityId: String(projectId || ""),
      currentField: expectedField,
      contextSummary: `Project: ${projectName || "Untitled project"}; project ID: ${projectId || "not created"}; section: ${currentStepName}; workflow status: ${workflowState}.`,
    });
  };

  const askLogan = async (question: string, explicitlyChosenField = "") => {
    const trimmed = question.trim();
    if (!trimmed || chatLoading || chatRequestInFlightRef.current) return;
    chatRequestInFlightRef.current = true;
    setMode("chat");
    onExpandedChange(true);
    setChatLoading(true);
    setChatError("");
    const optimistic: AssistantMessage = {
      id: `contract-user-${Date.now()}`,
      role: "user",
      message: trimmed,
      pending: true,
    };
    setMessages((current) => [...current, optimistic]);
    setInput("");
    try {
      if (pendingTurn && /^(yes|yep|yeah|haan|han|confirm|apply|fill it|go ahead|do it|kar do|apply kar do|ok|okay|theek hai)(?:\s+please)?[.!]?$/i.test(trimmed)) {
        await applyPendingChanges(optimistic.id);
        return;
      }
      if (pendingTurn && /^(no|nope|nahi|nahin|mat karo|cancel|discard|ignore)[.!]?$/i.test(trimmed)) {
        dismissPendingChanges(optimistic.id);
        return;
      }

      if (isContractFieldHelpQuestion(trimmed, expectedField)) {
        const reply = await requestGuideAnswer(trimmed);
        setMessages((current) => [
          ...current.map((message) => message.id === optimistic.id ? { ...message, pending: false } : message),
          {
            id: reply.messageId,
            role: "assistant",
            message: reply.answer,
            suggestions: reply.suggestions,
            sources: reply.sources,
          },
        ]);
        return;
      }

      const workflowTurn = await prepareContractWorkflowTurn(trimmed, workflowSnapshotRef.current, expectedField, editableFields);
      if (workflowTurn.intent === "question") {
        const reply = await requestGuideAnswer(trimmed);
        setMessages((current) => [
          ...current.map((message) => message.id === optimistic.id ? { ...message, pending: false } : message),
          {
            id: reply.messageId,
            role: "assistant",
            message: reply.answer,
            suggestions: reply.suggestions,
            sources: reply.sources,
          },
        ]);
        return;
      }
      setExpectedField(workflowTurn.nextField);
      if (workflowTurn.handled && workflowTurn.proposedChanges.length) {
        const requiresConfirmation = workflowTurn.proposedChanges.some((change) =>
          ["escrow", "milestones", "projectCostModel", "allowances"].includes(change.field) &&
          change.field !== explicitlyChosenField
        );
        if (!requiresConfirmation) {
          await onApplyChanges(workflowTurn.proposedChanges);
          workflowSnapshotRef.current = mergeWorkflowSnapshot(workflowSnapshotRef.current, workflowTurn.proposedChanges);
          const labels = workflowTurn.proposedChanges.map((change) => change.label.toLowerCase()).join(", ");
          const homeownerEmail = workflowTurn.proposedChanges.find((change) => change.field === "homeownerEmail")?.value;
          let followUp = workflowTurn.nextQuestion;
          if (typeof homeownerEmail === "string") {
            const matches = await onLookupHomeowners(homeownerEmail);
            setHomeownerMatches(matches);
            const homeownerFollowUp = matches.length
              ? `I found ${matches.length} matching homeowner${matches.length === 1 ? "" : "s"}. Select the contract signer below.`
              : "I couldn't find an existing homeowner with that email. Use Add Homeowner in the form, then continue here.";
            followUp = workflowTurn.nextField === "homeownerSelection"
              ? homeownerFollowUp
              : `${workflowTurn.nextQuestion} ${homeownerFollowUp}`;
          }
          setMessages((current) => [
            ...current.map((message) => message.id === optimistic.id ? { ...message, pending: false } : message),
            { id: `workflow-applied-${Date.now()}`, role: "assistant", message: `Added ${labels} to the draft. ${followUp}` },
          ]);
          setPendingTurn(null);
          return;
        }
        setPendingTurn(workflowTurn);
        setMessages((current) => [
          ...current.map((message) => message.id === optimistic.id ? { ...message, pending: false } : message),
          { id: `workflow-${Date.now()}`, role: "assistant", message: workflowTurn.message },
        ]);
        return;
      }
      if (expectedField) {
        setMessages((current) => [
          ...current.map((message) => message.id === optimistic.id ? { ...message, pending: false } : message),
          { id: `workflow-retry-${Date.now()}`, role: "assistant", message: workflowTurn.message || `I couldn't confidently identify that detail. ${workflowTurn.nextQuestion}` },
        ]);
        return;
      }

      const reply = await requestGuideAnswer(trimmed);
      setMessages((current) => [
        ...current.map((message) => message.id === optimistic.id ? { ...message, pending: false } : message),
        {
          id: reply.messageId,
          role: "assistant",
          message: reply.answer,
          suggestions: reply.suggestions,
          sources: reply.sources,
        },
      ]);
    } catch (error: any) {
      setMessages((current) => current.map((message) => message.id === optimistic.id ? { ...message, pending: false, failed: true } : message));
      setChatError(error?.response?.data?.message || "Logan could not respond. Try again in a moment.");
    } finally {
      chatRequestInFlightRef.current = false;
      setChatLoading(false);
    }
  };

  useEffect(() => {
    if (!autoFocus || !expanded) return;
    setMode("chat");
    const frame = window.requestAnimationFrame(() => composerRef.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [autoFocus, expanded]);

  useEffect(() => {
    const context = initialContext.trim();
    if (!context || initialContextProcessedRef.current) return;
    initialContextProcessedRef.current = true;
    setMode("chat");
    onExpandedChange(true);
    void askLogan(context);
    // The transferred context must be consumed once, not whenever form state changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialContext]);

  const applyPendingChanges = async (optimisticId = "") => {
    if (!pendingTurn) return;
    setChatLoading(true);
    setChatError("");
    try {
      await onApplyChanges(pendingTurn.proposedChanges);
      workflowSnapshotRef.current = mergeWorkflowSnapshot(workflowSnapshotRef.current, pendingTurn.proposedChanges);
      const labels = pendingTurn.proposedChanges.map((change) => change.label.toLowerCase()).join(", ");
      setMessages((current) => [
        ...current.map((message) => message.id === optimisticId ? { ...message, pending: false } : message),
        {
          id: `workflow-applied-${Date.now()}`,
          role: "assistant",
          message: `Added ${labels} to the form. ${pendingTurn.nextQuestion}`,
        },
      ]);
      setExpectedField(pendingTurn.nextField);
      setPendingTurn(null);
    } catch (error: any) {
      setMessages((current) => current.map((message) => message.id === optimisticId ? { ...message, pending: false, failed: true } : message));
      setChatError(error?.message || "Those changes could not be applied. Please try again.");
    } finally {
      setChatLoading(false);
    }
  };

  const dismissPendingChanges = (optimisticId = "") => {
    if (!pendingTurn) return;
    setMessages((current) => [
      ...current.map((message) => message.id === optimisticId ? { ...message, pending: false } : message),
      { id: `workflow-dismissed-${Date.now()}`, role: "assistant", message: `Okay, I did not change the form. ${pendingTurn.nextQuestion}` },
    ]);
    setExpectedField(pendingTurn.nextField);
    setPendingTurn(null);
  };

  const selectHomeowner = async (homeowner: ContractHomeownerMatch) => {
    setChatLoading(true);
    setChatError("");
    try {
      await onSelectHomeowner(homeowner);
      setHomeownerMatches([]);
      setMessages((current) => [
        ...current,
        { id: `workflow-homeowner-selected-${Date.now()}`, role: "assistant", message: `${homeowner.firstName || homeowner.email} is selected as the contract signer.` },
      ]);
    } catch (error: any) {
      setChatError(error?.message || "That homeowner could not be selected.");
    } finally {
      setChatLoading(false);
    }
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    void askLogan(input);
  };

  const handleComposerKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing) return;
    event.preventDefault();
    if (!input.trim() || chatLoading) return;
    void askLogan(input);
  };

  const handleSaveAndContinue = async () => {
    if (!chatContinueAction || actionLoading) return;
    setActionLoading(true);
    setChatError("");
    try {
      await onSaveAndContinue();
    } catch (error: any) {
      setChatError(error?.message || "This section could not be saved. Review the form and try again.");
    } finally {
      setActionLoading(false);
    }
  };

  if (!expanded) {
    return (
      <aside className="contract-ai-rail contract-ai-rail--collapsed" aria-label="Logan contract assistant">
        <button type="button" onClick={() => onExpandedChange(true)} aria-label="Open Logan contract assistant">
          <span><Sparkles size={18} /></span>
          <strong>Logan</strong>
          {nextItem && <small>{readinessItems.length - readyCount}</small>}
          <ChevronLeft size={17} />
        </button>
      </aside>
    );
  }

  return (
    <aside className="contract-ai-rail" aria-label="Logan contract assistant">
      <header className="contract-ai-rail__header">
        <div className="contract-ai-rail__identity">
          <span><Sparkles size={18} /></span>
          <div><strong>Logan</strong><small>{projectName || "New contract"}</small></div>
        </div>
        <button type="button" className="contract-ai-rail__collapse" onClick={() => onExpandedChange(false)} aria-label="Collapse Logan assistant"><ChevronRight size={18} /></button>
      </header>

      <div className="contract-ai-rail__status">
        <div><span>Contract readiness</span><strong>{readyCount}/{readinessItems.length}</strong></div>
        <div className="contract-ai-rail__progress" aria-hidden="true"><span style={{ width: `${(readyCount / readinessItems.length) * 100}%` }} /></div>
        <small>{workflowState} · Current: {currentStepName}</small>
      </div>

      <nav className="contract-ai-rail__tabs" aria-label="Logan assistant modes">
        <button type="button" className={mode === "assist" ? "is-active" : ""} onClick={() => setMode("assist")}><Sparkles size={14} /> Assist</button>
        <button type="button" className={mode === "chat" ? "is-active" : ""} onClick={() => setMode("chat")}><MessageSquareText size={14} /> Chat</button>
        <button type="button" className={mode === "review" ? "is-active" : ""} onClick={() => setMode("review")}><ClipboardCheck size={14} /> Review</button>
      </nav>

      {mode === "assist" && (
        <div className="contract-ai-rail__body">
          <section className="contract-ai-rail__section">
            <div className="contract-ai-rail__section-title"><span>Suggested next action</span></div>
            {nextItem ? (
              <button type="button" className="contract-ai-next" onClick={() => openStep(nextItem.step)} disabled={!projectId && nextItem.step > 1}>
                <span className="contract-ai-next__icon"><AlertCircle size={17} /></span>
                <span><strong>{nextItem.label}</strong><small>{nextItem.detail}</small></span>
                <ArrowRight size={16} />
              </button>
            ) : (
              <div className="contract-ai-complete"><CheckCircle2 size={18} /><div><strong>Contract complete</strong><small>All tracked contract items are ready.</small></div></div>
            )}
          </section>

          <section className="contract-ai-rail__section">
            <div className="contract-ai-rail__section-title"><span>Controls for {currentStepName}</span></div>
            <div className="contract-ai-controls">
              {activeStep === 1 && <>
                <button type="button" onClick={() => openStep(1)}><FileText size={15} /><span><strong>Review project fields</strong><small>Check project, address and homeowner</small></span></button>
                <button type="button" onClick={() => void askLogan("Review the current project information and explain what I should verify before creating the contract.")}><Sparkles size={15} /><span><strong>Ask Logan to review</strong><small>Get a contextual project checklist</small></span></button>
              </>}
              {activeStep === 2 && <>
                <button type="button" onClick={() => openStep(2)}><RefreshCw size={15} /><span><strong>Review milestone total</strong><small>{milestoneCount ? `${milestoneCount} milestones currently total ${milestoneTotal}%` : "No milestones added"}</small></span></button>
                <button type="button" onClick={() => void askLogan("Review my payment milestone plan. Explain any risks and suggest how the stages should be structured. Do not make changes without my approval.")}><Sparkles size={15} /><span><strong>Improve payment plan</strong><small>Ask for a safer milestone structure</small></span></button>
              </>}
              {activeStep === 4 && <>
                <button type="button" onClick={onOpenContractEditor}><FileText size={15} /><span><strong>{hasContractRecord ? "Open contract details" : "Complete contract terms"}</strong><small>Dates, scope, insurance and state rules</small></span></button>
                <button type="button" onClick={() => void askLogan("Explain the missing contract requirements for this project in plain language. Do not assume legal facts or submit anything.")}><Sparkles size={15} /><span><strong>Explain requirements</strong><small>Understand what is still needed</small></span></button>
              </>}
              {activeStep === 3 && <>
                <button type="button" onClick={() => void askLogan("Help me decide whether this project needs a subcontract agreement. Ask for any facts you need and do not create or send anything.")}><Sparkles size={15} /><span><strong>Check subcontract needs</strong><small>Optional branch; does not block the main contract</small></span></button>
              </>}
              {activeStep === 5 && <>
                <button type="button" onClick={() => setMode("review")}><ClipboardCheck size={15} /><span><strong>Review all blockers</strong><small>See generation and signature readiness</small></span></button>
              </>}
            </div>
          </section>

        </div>
      )}

      {mode === "chat" && (
        <div className="contract-ai-chat">
          <div className="contract-ai-chat__messages">
            {!messages.length && !chatLoading && (
              <div className="contract-ai-chat__welcome"><Sparkles size={22} /><strong>Build this contract with Logan</strong><p>Describe the project naturally. Logan will fill basic draft fields and ask only for the next missing detail.</p></div>
            )}
            {messages.map((message) => (
              <div key={message.id} className={`contract-ai-chat__message is-${message.role}${message.failed ? " is-failed" : ""}`}>
                <span>{message.role === "assistant" ? "Logan" : "You"}</span>
                <p>{message.message}</p>
                {message.role === "user" && (
                  <small className={`contract-ai-chat__delivery${message.failed ? " is-failed" : ""}`}>
                    {message.pending && <Loader2 size={10} className="contract-ai-spin" />}
                    {!message.pending && !message.failed && <Check size={10} />}
                    {message.failed ? "Not sent" : message.pending ? "Sending…" : "Sent"}
                  </small>
                )}
                {message.role === "assistant" && message.suggestions?.length ? (
                  <div className="contract-ai-chat__suggestions">
                    {message.suggestions.slice(0, 3).map((suggestion) => <button type="button" key={suggestion} onClick={() => void askLogan(suggestion)}>{suggestion}</button>)}
                  </div>
                ) : null}
              </div>
            ))}
            {choiceQuestion && !pendingTurn && (
              <section className="contract-ai-chat__choices" aria-label={`${choiceQuestion.label} options`}>
                <div className="contract-ai-chat__choices-title">
                  <strong>{choiceQuestion.selection === "checkbox" ? "Select all that apply" : "Choose one"}</strong>
                  <small>You can also type a different answer below.</small>
                </div>
                <div className="contract-ai-chat__choice-list" role={choiceQuestion.selection === "radio" ? "radiogroup" : "group"}>
                  {choiceQuestion.options.map((option) => {
                    const selected = selectedChoiceValue === option.value;
                    return (
                      <button
                        type="button"
                        key={option.value}
                        className={`contract-ai-chat__choice${selected ? " is-selected" : ""}`}
                        role={choiceQuestion.selection === "radio" ? "radio" : "checkbox"}
                        aria-checked={selected}
                        disabled={chatLoading}
                        onClick={() => void askLogan(option.prompt || option.label, expectedField)}
                      >
                        <span className="contract-ai-chat__choice-indicator">{selected && <Check size={11} />}</span>
                        <span className="contract-ai-chat__choice-copy">
                          <strong>{option.label}</strong>
                          {option.detail && <small>{option.detail}</small>}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </section>
            )}
            {homeownerMatches.length > 0 && (
              <section className="contract-ai-homeowners" aria-label="Matching homeowners">
                <div className="contract-ai-proposal__title"><UserRound size={14} /><strong>Matching homeowners</strong></div>
                {homeownerMatches.slice(0, 5).map((homeowner) => (
                  <button type="button" key={homeowner.id} onClick={() => void selectHomeowner(homeowner)} disabled={chatLoading}>
                    <span><strong>{[homeowner.firstName, homeowner.lastName].filter(Boolean).join(" ") || "Homeowner"}</strong><small>{homeowner.email}</small></span>
                    <span>Use</span>
                  </button>
                ))}
              </section>
            )}
            {pendingTurn && (
              <section className="contract-ai-proposal" aria-label="Proposed form changes">
                <div className="contract-ai-proposal__title"><Sparkles size={14} /><strong>Review before applying</strong></div>
                <div className="contract-ai-proposal__fields">
                  {pendingTurn.proposedChanges.map((change) => (
                    <div key={change.field}>
                      <span>{change.label}</span>
                      {Array.isArray(change.value) ? (
                        <div className="contract-ai-milestone-steps">
                          {change.value.map((milestone, index) => (
                            <div className="contract-ai-milestone-step" key={`${milestone.name}-${index}`}>
                              <span className="contract-ai-milestone-step__number">{index + 1}</span>
                              <span className="contract-ai-milestone-step__copy">
                                <strong>{milestone.name}</strong>
                                {milestone.description && <small>{milestone.description}</small>}
                              </span>
                              <span className="contract-ai-milestone-step__percent">{milestone.percentage}%</span>
                            </div>
                          ))}
                          <div className="contract-ai-milestone-total"><span>Total allocation</span><strong>100%</strong></div>
                        </div>
                      ) : (
                        <strong>{typeof change.value === "boolean" ? (change.value ? "Yes" : "No") : change.value}</strong>
                      )}
                    </div>
                  ))}
                </div>
                <p>This affects financial or payment terms, so Logan needs your approval. You can edit it afterward.</p>
                <div className="contract-ai-proposal__actions">
                  <button type="button" className="is-secondary" onClick={() => dismissPendingChanges()} disabled={chatLoading}>Dismiss</button>
                  <button type="button" className="is-primary" onClick={() => void applyPendingChanges()} disabled={chatLoading}>{chatLoading ? <Loader2 size={13} className="contract-ai-spin" /> : <Check size={13} />} Apply to form</button>
                </div>
              </section>
            )}
            {chatContinueAction && !pendingTurn && (
              <section className="contract-ai-chat-action" aria-label="Continue contract workflow">
                <div><CheckCircle2 size={15} /><span><strong>Section complete</strong><small>{chatContinueAction.detail}</small></span></div>
                <button type="button" onClick={() => void handleSaveAndContinue()} disabled={actionLoading || chatLoading}>
                  {actionLoading ? <Loader2 size={14} className="contract-ai-spin" /> : <ArrowRight size={14} />}
                  {actionLoading ? "Saving…" : chatContinueAction.label}
                </button>
              </section>
            )}
            {chatLoading && (
              <div className="contract-ai-chat__typing" role="status" aria-label="Logan is typing">
                <span className="contract-ai-chat__typing-avatar"><Sparkles size={12} /></span>
                <span className="contract-ai-chat__typing-copy">Logan is typing</span>
                <span className="contract-ai-chat__typing-dots" aria-hidden="true"><i /><i /><i /></span>
              </div>
            )}
            {chatError && <div className="contract-ai-chat__error" role="alert">{chatError}</div>}
            <div ref={messageEndRef} />
          </div>
          <form className="contract-ai-chat__composer" onSubmit={handleSubmit}>
            <textarea ref={composerRef} value={input} onChange={(event) => setInput(event.target.value)} onKeyDown={handleComposerKeyDown} placeholder={activeStep === 1 ? "Briefly describe your project…" : `Ask about ${currentStepName.toLowerCase()}…`} rows={2} />
            <div><small>Answer naturally or ask what any field means. Financial changes still require approval.</small><button type="submit" disabled={!input.trim() || chatLoading} aria-label="Send message"><Send size={16} /></button></div>
          </form>
        </div>
      )}

      {mode === "review" && (
        <div className="contract-ai-rail__body">
          <section className="contract-ai-rail__section">
            <div className="contract-ai-rail__section-title"><span>End-to-end checklist</span><small>{readinessItems.length - readyCount} remaining</small></div>
            <div className="contract-ai-checklist">
              {readinessItems.map((item) => (
                <button type="button" key={item.id} onClick={() => openStep(item.step)} disabled={!projectId && item.step > 1}>
                  <span className={item.ready ? "is-ready" : ""}>{item.ready ? <Check size={14} /> : <AlertCircle size={14} />}</span>
                  <span><strong>{item.label}</strong><small>{item.detail}</small></span>
                  <ChevronRight size={15} />
                </button>
              ))}
            </div>
          </section>
          <section className="contract-ai-rail__section contract-ai-rail__notice">
            <strong>Readiness states</strong>
            <p>Document readiness and both signatures are tracked separately so “ready to send” is never confused with “fully executed.”</p>
          </section>
        </div>
      )}
    </aside>
  );
};

function getNextContractQuestion(snapshot: LoganProjectDraft, editableFields: Set<string>) {
  const questions = [
    { field: "governingState", missing: !snapshot.governingState, question: "Which US state should govern this contract?" },
    { field: "startDate", missing: !snapshot.startDate, question: "What is the expected project start date?" },
    { field: "substantialCompletionDate", missing: !snapshot.substantialCompletionDate, question: "What is the expected substantial completion date?" },
    { field: "endDate", missing: !snapshot.endDate, question: "What is the final completion date?" },
    { field: "projectCostModel", missing: !snapshot.projectCostModel, question: "Which pricing model applies: total project cost, cost plus contractor fee, or time and materials?" },
    { field: "contractorInsurance", missing: !snapshot.contractorInsurance, question: "Does the contractor carry general liability insurance, have no coverage, or self-insure?" },
    { field: "workersCompensationInsurance", missing: !snapshot.workersCompensationInsurance, question: "Does the contractor carry workers' compensation insurance, or have no employees and qualify as exempt?" },
  ];
  return questions.find((item) => item.missing && editableFields.has(item.field)) || {
    field: "contractReview",
    question: "The editable contract details are complete. Review the form before generating the document.",
  };
}

function isContractFieldHelpQuestion(question: string, currentField = "") {
  const normalized = question
    .toLowerCase()
    .replace(/[’']/g, "")
    .replace(/[^a-z0-9&]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const fieldTerms = /\b(milestones?|payment plan|payment stage|escrow|insurance|general liability|workers? compensation|workers? comp|coverage limit|insurance provider|project name|scope of works?|work description|job site|project address|home ?owner|project value|project cost|total sales price|governing (state|law)|start date|substantial completion|final completion|completion date|cost model|pricing model|fixed price|cost plus|time (and|&) materials|allowances?|authorized representative|federal tax id|tax id|ein|pdf file name|document name|signatures?|contract readiness|contract status|subcontract(or)?|contract fields?|project fields?)\b/;
  const helpIntent = /\b(what|why|how|which|when|where|explain|meaning|mean|define|purpose|required|difference|enter|fill|help|kya|kyu|kyun|kyon|kaise|kisliye|kaunsa|konsa|kab|kahan|matlab|mtlb|samjhao|samjha|bata|batao|bharna|bharu|dalna|chahiye|zaroori|jaruri|jruri)\b/;
  const strongQuestionIntent = /\b(what|why|how|which|when|where|explain|meaning|mean|define|purpose|difference|help|kya|kyu|kyun|kyon|kaise|kisliye|kaunsa|konsa|kab|kahan|matlab|mtlb|samjhao|samjha|bata|batao|zaroori|jaruri|jruri)\b/;
  const overviewIntent = /\b(all|every|each|whole|complete|full|pure|saare|sare|har|hr)\b.*\b(field|fields|contract|project)\b/
    .test(normalized)
    || /\b(contract|project)\b.*\b(all|every|each|whole|complete|full|pure|saare|sare|har|hr)\b.*\b(field|fields)?\b/
      .test(normalized);
  const exactFieldName = /^(milestones?|payment plan|escrow|insurance|general liability|workers? compensation|workers? comp|insurance provider|project name|scope of works?|job site|project address|home ?owner|project value|project cost|governing state|governing law|start date|substantial completion|final completion|completion date|cost model|pricing model|allowances?|authorized representative|federal tax id|tax id|ein|pdf file name|document name|signatures?|contract readiness|contract status|subcontract(or)?)$/.test(normalized);
  const asksAboutCurrentField = Boolean(currentField) && strongQuestionIntent.test(normalized);
  return overviewIntent || exactFieldName || asksAboutCurrentField || (fieldTerms.test(normalized) && helpIntent.test(normalized));
}

function mergeWorkflowSnapshot(snapshot: LoganProjectDraft, changes: ContractWorkflowChange[]): LoganProjectDraft {
  return changes.reduce((next, change) => ({ ...next, [change.field]: change.value }), { ...snapshot });
}

interface WorkflowChoiceOption {
  label: string;
  value: string;
  prompt?: string;
  detail?: string;
}

interface WorkflowChoiceQuestion {
  label: string;
  selection: "radio" | "checkbox";
  options: WorkflowChoiceOption[];
}

function getWorkflowChoiceQuestion(field: string): WorkflowChoiceQuestion | null {
  switch (field) {
    case "governingState":
      return {
        label: "Governing law state",
        selection: "radio",
        options: [
          { label: "Connecticut", value: "CT" },
          { label: "Florida", value: "FL" },
          { label: "Massachusetts", value: "MA" },
          { label: "Delaware", value: "DE" },
          { label: "Ohio", value: "OH" },
        ],
      };
    case "projectCostModel":
      return {
        label: "Pricing model",
        selection: "radio",
        options: [
          { label: "Total project cost", value: "Total Cost of the Project", detail: "One agreed contract amount" },
          { label: "Cost plus contractor fee", value: "Cost of the Work plus the Contractor’s Fee", detail: "Actual work cost plus an agreed fee" },
          { label: "Time and materials", value: "Time and Materials", detail: "Labor time and material costs" },
        ],
      };
    case "contractorInsurance":
      return {
        label: "General liability insurance",
        selection: "radio",
        options: [
          { label: "Has coverage", value: "yes", prompt: "The contractor carries general liability insurance." },
          { label: "No coverage", value: "no", prompt: "The contractor does not carry general liability insurance." },
          { label: "Self-insured", value: "selfInsured", prompt: "The contractor is self-insured." },
        ],
      };
    case "workersCompensationInsurance":
      return {
        label: "Workers’ compensation",
        selection: "radio",
        options: [
          { label: "Has insurance", value: "hasInsurance", prompt: "The contractor carries workers compensation insurance." },
          { label: "No employees / exempt", value: "noEmployees", prompt: "The contractor has no employees and qualifies as exempt from workers compensation." },
        ],
      };
    default:
      return null;
  }
}

export default ContractAssistantRail;
